/**
 * Editing node labels.
 *
 * Labels are how a workload chooses its nodes - nodeSelector and nodeAffinity
 * match on labels and nothing else. In particular the OS distribution lives in
 * status.nodeInfo.osImage, which no selector can reach, so targeting "the
 * AlmaLinux boxes" means labelling them first.
 */

/**
 * Prefixes Kubernetes owns. The kubelet sets these on registration and will
 * overwrite anything we write, so they are shown but never editable.
 */
const PROTECTED_PREFIXES = ["kubernetes.io/", "beta.kubernetes.io/"];

export const isProtectedLabel = (key: string): boolean =>
    PROTECTED_PREFIXES.some((prefix) => key.indexOf(prefix) === 0);

const NAME = /^[A-Za-z0-9]([-A-Za-z0-9_.]{0,61}[A-Za-z0-9])?$/;
const DNS_SUBDOMAIN = /^[a-z0-9]([-a-z0-9.]{0,251}[a-z0-9])?$/;

/**
 * Validate a label key against Kubernetes' rules, so a bad key is rejected here
 * rather than coming back as an opaque 422 from the API server.
 */
export const validateLabelKey = (key: string): string => {
    if (!key) {
        return "A label key is required.";
    }
    if (isProtectedLabel(key)) {
        return "Kubernetes manages this label; it cannot be edited here.";
    }

    const slash = key.indexOf("/");

    if (slash >= 0) {
        const prefix = key.slice(0, slash);
        const name = key.slice(slash + 1);

        if (!DNS_SUBDOMAIN.test(prefix)) {
            return "The part before the slash must be a DNS subdomain.";
        }
        if (!NAME.test(name)) {
            return "The part after the slash must be 63 characters or fewer of letters, digits, dashes, underscores or dots.";
        }

        return "";
    }

    return NAME.test(key)
        ? ""
        : "A key must be 63 characters or fewer of letters, digits, dashes, underscores or dots.";
};

/** Values follow the same rules as a key's name part, and may be empty. */
export const validateLabelValue = (value: string): string =>
    !value || NAME.test(value)
        ? ""
        : "A value must be 63 characters or fewer of letters, digits, dashes, underscores or dots.";

export interface LabelEdit {
    /** Labels to set or overwrite. */
    readonly set: { [key: string]: string };
    /** Label keys to remove. */
    readonly remove: string[];
}

/**
 * The strategic merge patch body for a label change.
 *
 * A merge patch removes a key by setting it to null, which is how kubectl label
 * --overwrite and `key-` are implemented.
 */
export const buildLabelPatch = (edit: LabelEdit): { metadata: { labels: { [key: string]: string | null } } } => {
    const labels: { [key: string]: string | null } = {};

    Object.keys(edit.set)
        .filter((key) => !isProtectedLabel(key))
        .forEach((key) => {
            labels[key] = edit.set[key];
        });

    edit.remove
        .filter((key) => !isProtectedLabel(key))
        .forEach((key) => {
            labels[key] = null;
        });

    return { metadata: { labels } };
};

/** Split "key=value" as typed into a bulk-apply field. A bare key sets an empty value. */
export const splitLabelInput = (input: string): { key: string; value: string } => {
    const trimmed = (input || "").trim();
    const index = trimmed.indexOf("=");

    return index < 0
        ? { key: trimmed, value: "" }
        : { key: trimmed.slice(0, index).trim(), value: trimmed.slice(index + 1).trim() };
};
